import React, { useState } from 'react';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import ModalAction from '@/components/ModalAction';
import LoaderIcon from '@/images/icons/Loader';
import { fetchAPI } from '@/api';
import { AuthState } from '@/types';
import { Input } from '@/components/Form/Input';
import { useNavigate } from 'react-router-dom';

const TransferModal = ({ modalOpen, setModalOpen, modalMessage, modalError, setModalMessage, setModalError, isConfirmed, setIsConfirmed, projectId }: any) => {
	const auth = useSelector((state: { auth: AuthState }) => state.auth);
	const navigate = useNavigate();

	const [transferInput, setTransferInput] = useState('');
	const [transferInputConfirm, setTransferInputConfirm] = useState('');
	const [newTelegramUserId, setNewTelegramUserId] = useState(null);
	const [doubleCheck, setDoubleCheck] = useState(false);
	const [isLoading, setIsLoading] = useState(false);

	const onClearModal = () => {
		setModalOpen(false);
		setTimeout(() => {
			setTransferInput('');
			setTransferInputConfirm('');
			setNewTelegramUserId(null);
			setDoubleCheck(false);
			setIsLoading(false);
			setModalMessage(null);
			setModalError(null);
			setIsConfirmed(false);
		}, 400);
	};

	const onValidateUser = async () => {
		setIsLoading(true);
		setModalMessage(null);
		setModalError(null);

		if (transferInput.length === 0) {
			setModalError('Please type the new owner\'s Telegram ID');
			setIsLoading(false);
			return;
		}

		try {
			const response = await fetchAPI("/project/transfer-validate", auth?.user?.token, {
				projectId,
				newTelegramUserId: transferInput,
			});
			if (!response.ok) {
				throw new Error(`HTTP error: ${response.status}`);
			}

			const data = await response.json();

			if (data) {
				if (data.newTelegramUserId) {
					// @ts-ignore
					setNewTelegramUserId(parseInt(data.newTelegramUserId, 10));
					setDoubleCheck(true);
				}
			}
		} catch (error: any) {
			console.error('Error fetching data:', error);
			let errorMessage = 'An unexpected error occurred';
			if (error.response?.message) {
				errorMessage = error.response.message;
			}
			setModalError(errorMessage);
		} finally {
			setIsLoading(false);
		}
	};

	const onTransferProject = async () => {
		setIsLoading(true);
		setModalMessage(null);
		setModalError(null);

		if (transferInputConfirm.length === 0) {
			setModalError('Please type the new owner\'s Telegram ID');
			setIsLoading(false);
			return;
		}

		if (newTelegramUserId !== parseInt(transferInputConfirm, 10)) {
			setModalError('Fields are not equal. Please type the new owner\'s Telegram ID again.');
			return;
		}

		try {
			const response = await fetchAPI("/project/transfer", auth?.user?.token, {
				projectId,
				newTelegramUserId: newTelegramUserId,
				newTelegramUserIdConfirm: parseInt(transferInputConfirm, 10),
			});
			if (!response.ok) {
				throw new Error(`HTTP error: ${response.status}`);
			}

			const data = await response.json();

			if (data) {
				if (data.project) {
					navigate(
						'/transferred-project',
						{
							state: {
								projectName: data.project.name,
								telegramId: newTelegramUserId,
							}
						}
					)
				}
			}
		} catch (error: any) {
			console.error('Error fetching data:', error);
			let errorMessage = 'An unexpected error occurred';
			if (error.response?.message) {
				errorMessage = error.response.message;
			}
			setModalError(errorMessage);
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<ModalAction
			id="transfer-modal"
			modalOpen={modalOpen}
			setModalOpen={setModalOpen}
			onModalClose={() => onClearModal()}
			size={undefined}
		>
			<h2 className="h2 mb-4 pr-16 md:pr-14 leading-[0.9]">
				Transfer project
			</h2>
			<div className={classNames(
				doubleCheck ? 'hidden' : 'visible',
			)}>
				<div>To transfer your project to the new owner, please send the new owner's Telegram ID</div>
				<div>To find out his ID, the new owner can send <span className="text-link-blue">/id</span> command to <a
					href="https://t.me/InviteMemberBot"
					target="_blank"
					rel="noopener noreferrer"
					className="text-link-blue hover:underline">@InviteMemberBot</a>.
				</div>
				<Input className="h-11 mt-4"
				       type="text"
				       name="projectTransferValidate"
				       value={transferInput}
				       onChange={(e) => setTransferInput(e.target.value)}
				       required
				>
					Telegram ID
				</Input>
				<button className="btn-primary w-full mt-8" onClick={() => onValidateUser()}>
					{isLoading ? (
						<div className="flex items-center justify-center py-1 pointer-events-none">
							<LoaderIcon className="animate-rotation w-6 h-6 text-white" />
						</div>
					) : (
						<span className="pointer-events-none">Transfer</span>
					)}
				</button>
			</div>
			<div className={classNames(
				doubleCheck ? 'visible' : 'hidden',
			)}>
				<div>
					Are you <b>sure</b> want to transfer your project to the new owner?
						<div>This operation can't be rolled back.</div>
				</div>
				<div>To confirm, please type the new owner ID again.</div>
				<Input className="h-11 mt-4 test"
				       type="text"
				       name="projectTransfer"
				       value={transferInputConfirm}
				       onChange={(e) => setTransferInputConfirm(e.target.value)}
				       required>
				</Input>
				<button className="btn-primary w-full mt-8" onClick={() => onTransferProject()}>
					{isLoading ? (
						<div className="flex items-center justify-center py-1 pointer-events-none">
							<LoaderIcon className="animate-rotation w-6 h-6 text-white" />
						</div>
					) : (
						<span className="pointer-events-none">Transfer project</span>
					)}
				</button>
			</div>
			{/*<button className="btn-primary-inverted w-full mt-4" onClick={() => onClearModal()}>*/}
			{/*	<span className="pointer-events-none">Cancel</span>*/}
			{/*</button>*/}
			{modalError && (
				<div className="text-sm text-red-orange mt-2 font-medium">{modalError}</div>
			)}
			{modalMessage && modalMessage}
		</ModalAction>
	);
};

export default TransferModal;
